/**
 * 手牌组件 - 扑克牌竖版高窄布局
 * 
 * 颜色由卡牌类型决定（话题=蓝、事件=橙、人设=紫）
 * 左上角小徽章显示风险等级（safe=绿、normal=橙、risky=红）
 */

import React from 'react';
import { Star, Zap } from 'lucide-react';
import type { GameCard } from '../../../types';
import { CARD_TYPE_STYLE_CONFIG, RISK_STYLES, CARD_TYPE_ICON } from '../../../constants/styles';

interface HandCardProps {
  card: GameCard;
  /** 是否可点击 */
  isClickable: boolean;
  /** 点击回调 */
  onClick?: () => void;
}

/**
 * 手牌组件 - 扑克牌竖版高窄布局
 * 
 * 尺寸比例接近真实扑克牌 (2.5:3.5 = 1:1.4)
 */
export const HandCard: React.FC<HandCardProps> = React.memo(({
  card,
  isClickable,
  onClick,
}) => {
  const typeConfig = CARD_TYPE_STYLE_CONFIG[card.type];
  const riskStyle = RISK_STYLES[card.riskLevel];
  const TypeIcon = CARD_TYPE_ICON[card.type] || Zap;

  return (
    <button
      disabled={!isClickable}
      onClick={onClick}
      className={`
        relative
        w-[80px] sm:w-[100px]
        aspect-[2/3.2]
        flex-shrink-0
        ${typeConfig.bgGradient}
        border-2 ${typeConfig.border}
        rounded-lg
        ${typeConfig.shadow}
        ${typeConfig.glowClass}
        overflow-hidden
        ${isClickable ? 'cursor-pointer' : ''}
        transition-all duration-300 ease-out
        ${isClickable ? 'hover:shadow-xl hover:-translate-y-2 hover:rotate-2 active:scale-95' : ''}
        group
        flex flex-col
      `}
    >
      {/* 装饰性背景图案 - 扑克牌纹理 */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 15% 15%, currentColor 0.5px, transparent 0.5px),
            radial-gradient(circle at 85% 85%, currentColor 0.5px, transparent 0.5px)
          `,
          backgroundSize: '8px 8px',
        }} />
      </div>

      {/* === 顶部区域: 风险等级徽章 + 类型标签 === */}
      <div className="relative flex justify-between items-start p-1.5 sm:p-2 z-10">
        {/* 左上角: 风险等级小徽章 */}
        <div className={`
          w-5 h-5 sm:w-6 sm:h-6
          ${riskStyle.badge}
          rounded-full
          flex items-center justify-center
          text-white text-[7px] font-bold
          shadow-sm
          border border-white/50
        `} title={riskStyle.label}>
          {riskStyle.icon}
        </div>

        {/* 右上角: 类型标签 */}
        <div className={`
          text-[8px] sm:text-[9px] font-medium
          ${typeConfig.textColor}
          bg-white/70
          px-1 py-0.5 sm:px-1.5
          rounded
          border ${typeConfig.border}
        `}>
          {card.type}
        </div>
      </div>

      {/* === 中部区域: 类型图标 === */}
      <div className="flex-1 flex items-center justify-center relative">
        {/* 装饰圆环 */}
        <div className={`
          absolute
          w-10 h-10 sm:w-16 sm:h-16
          rounded-full
          border-2 border-dashed ${typeConfig.border}
          opacity-20
          ${isClickable ? 'group-hover:animate-spin' : ''}
          transition-all duration-1000
        `} style={{ animationDuration: '10s' }} />

        {/* 内圈 */}
        <div className={`
          absolute
          w-8 h-8 sm:w-12 sm:h-12
          rounded-full
          ${typeConfig.iconBg}
          opacity-50
        `} />

        {/* 主图标 */}
        <div className={`
          relative
          w-9 h-9 sm:w-14 sm:h-14
          ${typeConfig.iconBg}
          rounded-xl sm:rounded-2xl
          flex items-center justify-center
          border-2 ${typeConfig.border}
          shadow-lg
          ${isClickable ? 'group-hover:scale-110 group-hover:rotate-6' : ''}
          transition-transform duration-300
        `}>
          <TypeIcon className={`w-5 h-5 sm:w-7 sm:h-7 ${typeConfig.iconColor}`} strokeWidth={2} />
        </div>

        {/* 四角小装饰 */}
        <div className={`absolute top-0 left-1 sm:left-3 ${typeConfig.accentColor} opacity-40`}>
          <Star className="w-2 h-2" fill="currentColor" />
        </div>
        <div className={`absolute top-0 right-1 sm:right-3 ${typeConfig.accentColor} opacity-40`}>
          <Star className="w-2 h-2" fill="currentColor" />
        </div>
        <div className={`absolute bottom-0 left-1 sm:left-3 ${typeConfig.accentColor} opacity-40`}>
          <Star className="w-2 h-2" fill="currentColor" />
        </div>
        <div className={`absolute bottom-0 right-1 sm:right-3 ${typeConfig.accentColor} opacity-40`}>
          <Star className="w-2 h-2" fill="currentColor" />
        </div>
      </div>

      {/* === 底部区域: 卡牌名称 + CP === */}
      <div className="relative p-1 sm:p-2 z-10 flex flex-col items-center gap-0.5 sm:gap-1">
        {/* 卡牌名称 */}
        <div className={`
          text-[10px] sm:text-xs font-bold ${typeConfig.textColor}
          text-center leading-tight
          line-clamp-2
          w-full
        `}>
          {card.name}
        </div>

        {/* CP 范围 */}
        <div className={`
          text-[8px] sm:text-[10px] font-medium
          ${typeConfig.textColor}
          opacity-70
          bg-white/50
          px-1.5 py-0.5
          rounded-full
        `}>
          {card.cpRange} CP
        </div>
      </div>

      {/* 顶部装饰线 */}
      <div className={`absolute top-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-current to-transparent ${typeConfig.accentColor} opacity-30`} />
      
      {/* 底部装饰线 */}
      <div className={`absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-current to-transparent ${typeConfig.accentColor} opacity-30`} />
    </button>
  );
});

HandCard.displayName = 'HandCard';
